import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import KillSwitch from "@/components/KillSwitch";
import ModeToggle from "@/components/ModeToggle";
import NotificationBell from "@/components/NotificationBell";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  House, Pulse, Briefcase, Plug, Brain, ClockCounterClockwise, Sliders, SignOut,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/dashboard", label: "Cockpit", icon: House, testId: "nav-dashboard" },
  { to: "/signals", label: "AI Signals", icon: Brain, testId: "nav-signals" },
  { to: "/portfolio", label: "Portfolio", icon: Briefcase, testId: "nav-portfolio" },
  { to: "/brokers", label: "Brokers", icon: Plug, testId: "nav-brokers" },
  { to: "/history", label: "History", icon: ClockCounterClockwise, testId: "nav-history" },
  { to: "/settings", label: "Risk Center", icon: Sliders, testId: "nav-settings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [settings, setSettings] = useState(null);

  const loadSettings = async () => {
    try {
      const r = await api.get("/risk/settings");
      setSettings(r.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { loadSettings(); }, []);

  const handleLogout = () => {
    logout();
    nav("/login");
  };

  const mode = settings?.execution_mode || "manual";
  const killActive = settings?.kill_switch_active || false;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--bg)" }}>
      {/* Top bar */}
      <header className="border-b border-[#272A30] bg-[#0A0D14] flex items-center justify-between h-14 px-4 z-20" data-testid="top-bar">
        <div className="flex items-center gap-6">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="brand-link">
            <span className="inline-block w-2 h-2 bg-[#00E5FF]" />
            <span className="font-semibold tracking-tight">QUANTBRIDGE</span>
            <span className="text-[10px] text-[#4B5563] font-mono uppercase tracking-[0.3em]">/ AI TRADING DESK</span>
          </Link>
        </div>
        <div className="flex items-center gap-3">
          <ModeToggle
            mode={mode}
            autoConsent={settings?.auto_consent_acknowledged}
            killSwitch={killActive}
            onChange={(s) => setSettings(s)}
          />
          <NotificationBell />
          <KillSwitch active={killActive} onChange={(active) => setSettings({ ...(settings||{}), kill_switch_active: active })} />
          <div className="flex items-center gap-2 pl-3 ml-1 border-l border-[#272A30]">
            <div className="w-7 h-7 rounded-sm bg-[#1A1D24] border border-[#272A30] flex items-center justify-center text-[10px] font-mono">
              {(user?.full_name || "U").substring(0,1).toUpperCase()}
            </div>
            <button onClick={handleLogout} className="text-[10px] uppercase tracking-[0.2em] text-[#8B95A5] hover:text-white" data-testid="logout-btn">
              <SignOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Side nav */}
        <aside className="w-[200px] border-r border-[#272A30] bg-[#0A0D14] py-4">
          <nav className="flex flex-col">
            {NAV.map((n) => {
              const Icon = n.icon;
              const active = loc.pathname.startsWith(n.to);
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  className="nav-item"
                  data-active={active}
                  data-testid={n.testId}
                >
                  <Icon size={14} weight="bold" />
                  <span>{n.label}</span>
                </Link>
              );
            })}
          </nav>
          <div className="mt-6 px-4 text-[10px] text-[#4B5563] uppercase tracking-[0.2em] font-mono">
            <div className="border-t border-[#272A30] pt-3">
              <div className="flex items-center gap-2 mb-2">
                <Pulse size={10} className="text-[#00E676]" />
                <span>Demo · Sandbox</span>
              </div>
              <div className="text-[#4B5563]">No real funds at risk.</div>
            </div>
          </div>
        </aside>

        <main className="flex-1 overflow-x-hidden">{children}</main>
      </div>
    </div>
  );
}
